/**
 * event-binding-shape.ts — the ONE place the "where are the event bindings?"
 * fact lives, in the package every publisher already depends on.
 *
 * THE SHAPE IS A FACT, read from the runtime. cogo-runtime-v2 accepts three
 * spellings and they are semantically identical:
 *
 *   app-runtime-core.ts:1617  events.bindings   — flat `elementId:eventName` map
 *   app-runtime-core.ts:1699  events.elements   — { elementId: { eventName: binding } }
 *   app-runtime-core.ts:1685  top-level         — { elementId: { eventName: binding } }
 *                                                 directly under `events`
 *   render-pack.ts:415        events.elements   — the Zero-Copy builder reads ONLY this one
 *
 * The agentic generator emits `elements` 100% of the time (357/357 committed
 * events.json), while every publisher read `bundle.events.bindings` alone:
 * 1,188 declared bindings produced 0 handlers and 0 diagnostics, so a published
 * app had no interactivity at all and nothing said so.
 *
 * WHY IT LIVES HERE. Task 4 first folded the shapes inside
 * `@cogo/application-package`'s ingest, which serves @cogo/publisher-next,
 * -cogo-web, -flutter and @cogo/publishing-application. A review then found
 * @cogo/publisher-newwon does NOT go through that ingest — it declares exactly
 * one `@cogo` dependency (`@cogo/publisher-core`) and builds blocks straight
 * from raw JSON in `read-app.ts` and `read-package-dir.ts`, so it still emitted
 * zero handlers for generated apps. `@cogo/publisher-core` is the one package
 * all of them already depend on, so the fact lives here and
 * `@cogo/application-package` re-exports it rather than keeping a second copy.
 *
 * Mirrors `@cogo/validator` src/utils/event-bindings.ts#normalizeEventBindings,
 * whose own header warns that "reading `events.bindings` alone reproduces the
 * blindness this module exists to fix".
 */

/** Keys of the events artifact that are NOT an `{ eventName: binding }` map. */
export const EVENT_ARTIFACT_RESERVED_KEYS: ReadonlySet<string> = new Set([
  "bindings",
  "elements",
  "lifecycle",
  "schemaVersion",
  "blockType",
]);

/** Declared artifact fields carried through normalization verbatim. */
export const EVENT_ARTIFACT_CARRIED_KEYS: ReadonlySet<string> = new Set([
  "schemaVersion",
  "blockType",
  "lifecycle",
]);

/**
 * Merge every runtime-accepted binding spelling into one flat map keyed
 * `${elementId}:${eventName}`.
 *
 * Precedence: an explicit `bindings` entry WINS over a nested one for the same
 * key (first-writer-wins, matching `normalizeEventBindings`). Idempotent —
 * feeding the result back through produces the same map.
 */
export function foldBundleEventBindings<TBinding = Record<string, unknown>>(
  eventsInput: unknown,
): Record<string, TBinding> {
  const merged: Record<string, TBinding> = {};
  if (
    !eventsInput ||
    typeof eventsInput !== "object" ||
    Array.isArray(eventsInput)
  ) {
    return merged;
  }
  const events = eventsInput as Record<string, unknown>;

  const flat = events.bindings;
  if (flat && typeof flat === "object" && !Array.isArray(flat)) {
    for (const [key, binding] of Object.entries(flat)) {
      merged[key] = binding as TBinding;
    }
  }

  const addEventMap = (elementId: string, eventMap: unknown): void => {
    if (!eventMap || typeof eventMap !== "object" || Array.isArray(eventMap)) {
      return;
    }
    for (const [eventName, binding] of Object.entries(
      eventMap as Record<string, unknown>,
    )) {
      if (!binding || typeof binding !== "object" || Array.isArray(binding)) {
        continue;
      }
      const key = `${elementId}:${eventName}`;
      if (!(key in merged)) merged[key] = binding as TBinding;
    }
  };

  const nested = events.elements;
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    for (const [elementId, eventMap] of Object.entries(
      nested as Record<string, unknown>,
    )) {
      addEventMap(elementId, eventMap);
    }
  }

  for (const [key, value] of Object.entries(events)) {
    if (EVENT_ARTIFACT_RESERVED_KEYS.has(key)) continue;
    addEventMap(key, value);
  }

  return merged;
}
